import { call } from "redux-saga/effects";
import action from "../../decorators/action.decorator";
import reducer, { InitSchemaReducer } from "../../decorators/reducer.decorator";
import { DIContainerKeys } from "../../di-container/di-container.keys";
import { Entities } from "../../store/entities/entities.enum";
import { HttpService } from "../http.service";
import { ReducerMethods } from "../../store/reducer.methods";
import { API_DOMENS, BASE_API_PAGE_ENUMS, BASE_API_PATHS } from "../../../constants/apis.constants.js";
import loader from "../../decorators/loader.decorator";
import { Loaders } from "../../store/loaders/loaders.enum";
import { CallToast } from "../../../Components/PriceCalculator/modals/CustomToast";
import flagger from "../../decorators/flagger.decorator";
import { Flaggers } from "../../store/flaggers/flaggers.enum";
import type { GetPracticeToolsByIdDto, GetPracticeToolsByIdResponse } from "./dto/get-practice-tools-by-id.dto";
import type { RemovePracticeToolDto, RemovePracticeToolResponse } from "./dto/remove-practice-tool.dto";

export const PracticeEffectActions = {
  getPracticeToolsById: `${Entities.PracticeTools}_getPracticeToolsById`,
  removePracticeTool: `${Entities.PracticeTools}_removePracticeTool`,
};

const initSchema: InitSchemaReducer = {
  definitions: {},
  options: { idAttribute: '' },
};

@reducer({
  entityName: Entities.PracticeTools,
  initSchema,
  aliasName: DIContainerKeys.PracticeService,
})
export class PracticeService extends HttpService {
  @loader(Loaders.GetPracticeToolsByIdLoader)
  @action(DIContainerKeys.PracticeService)
  public *getPracticeToolsById(dto: GetPracticeToolsByIdDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.GetPracticeToolsByIdLoader, true);
    
    const response: GetPracticeToolsByIdResponse = yield call(
      this.post,
      {
        url: BASE_API_PATHS.getPracticesTools,
        body: this.toFormData({
          ToolId: dto.ToolId,
        }),
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );
    
    if (!response?.mr?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.GetPracticeToolsByIdLoader, false);
      return;
    }
    
    const { mr, ...payload } = response;
    yield this.setEntityStateById(Entities.PracticeTools, dto.ToolId, payload);
    yield this.setLoaderState(Loaders.GetPracticeToolsByIdLoader, false);
  }

  @loader(Loaders.RemovePracticeToolLoader)
  @flagger(Flaggers.RemovePracticeToolFlagger)
  @action(DIContainerKeys.PracticeService)
  public *removePracticeTool(dto: RemovePracticeToolDto): Generator<any, void, any> {
    const compositeKey = `${dto.practiceId}-${dto.toolId}`;
    const currentFlaggerState = this.di.reduxStore.state()?.[Flaggers.RemovePracticeToolFlagger] ?? {};
    
    yield this.setFlaggerState(Flaggers.RemovePracticeToolFlagger, {
      ...currentFlaggerState,
      [compositeKey]: true,
    });
    yield this.setLoaderState(Loaders.RemovePracticeToolLoader, true);
    
    const response: RemovePracticeToolResponse = yield call(
      this.get,
      {
        url: this.pageEnum(BASE_API_PAGE_ENUMS.RemovePracticeTool),
        params: {
          optionEnum1: dto.practiceId,
          optionEnum2: dto.toolId,
          optionEnum3: dto.clientId,
        },
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );

    if (!response?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
    } else {
      CallToast("Practice tool removed successfully", true, false, "v2style");
    }
    const latestFlaggerState = this.di.reduxStore.state()?.[Flaggers.RemovePracticeToolFlagger] ?? {};
    
    yield this.setFlaggerState(Flaggers.RemovePracticeToolFlagger, {
      ...latestFlaggerState,
      [compositeKey]: false,
    });
    yield this.setLoaderState(Loaders.RemovePracticeToolLoader, false);
    
    if (dto.callback) {
      dto.callback();
    }
  }
}
